import { RICH, STRICT } from '../utils/spds/html-verification';
import { helper } from '@ember/component/helper';
import { htmlSafe } from '@ember/template';
import { sanitize } from '../utils/spds/sanitize-html';

/**
 * Sanitizes an HTML string and marks the result as trusted. A drop-in replacement for `htmlSafe`
 * anywhere the string is consumer-supplied rather than developer-authored.
 *
 * The value stays the first positional parameter so the call reads identically in a JS getter and
 * in a `.gjs` template, where an imported plain function works in mustache position:
 *
 * ```js
 * get content() {
 *   return sanitizeHtml(this.args.content, STRICT);
 * }
 * ```
 *
 * ```handlebars
 * <M.Body>{{sanitizeHtml modal.bodyText}}</M.Body>
 * ```
 * @param {unknown} value - String, `SafeString`, or finite number; anything else renders empty.
 * @param {object} [policy] - `STRICT` or `RICH` from `utils/spds/html-verification`. Defaults to
 *   `RICH`; tooltips pass `STRICT`.
 * @returns {import('@ember/template/-private/handlebars').SafeString}
 */
export function sanitizeHtml(value, policy = RICH) {
  return htmlSafe(sanitize(value, policy));
}

/**
 * Resolver-registered form, so consuming apps get `{{spds-sanitize-html someString}}` in classic
 * `.hbs` templates for their own `htmlSafe` call sites.
 *
 * The policy arrives as a named argument here rather than a second positional one, because
 * plain-function helpers receive named args as a trailing object, which would collide with a
 * positional policy in template position.
 *
 * ```handlebars
 * {{spds-sanitize-html this.userSuppliedString policy="strict"}}
 * ```
 */
export default helper(([value], { policy }) =>
  sanitizeHtml(value, policy === 'strict' ? STRICT : RICH)
);
